import { OpportunityCard, OpportunityCardContent } from './OpportunityCard';
import { Eye } from 'lucide-react';
import { createOpportunityClientsTableColumns } from './OpportunityClientsTable';
import { DataTable } from './DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState, useMemo } from 'react';
import {
  EngagementSheetPrimaryBtn,
  OpportunityEngagementSheet,
} from '@/components/OpportunityEngagementSheet';
import { reviewOnboardingSlidesReminderWithLinkTemplate } from '@shared';
import {
  useHandleEmailSentStatus,
  useOpportunityState,
} from '@/pages/AdvisorDashboardOpportunities';
import { SendOpportunityMessageModal } from './SendOpportunityMessageModal';

export function OpportunityResendClientMagicLinks() {
  const {
    advisorOpportunitiesState,
    status,
    priorityStatus,
    qualifyingClients,
    clientsEligibleForReminder,
  } = useOpportunityState('RESEND_CLIENT_SIGNIN_LINKS');
  const [sheetOpen, setSheetOpen] = useState(false);

  useHandleEmailSentStatus(
    advisorOpportunitiesState,
    () => setSheetOpen(false),
    'RESEND_CLIENT_SIGNIN_LINKS',
  );

  const engagementSheet = useMemo(
    () => (
      <OpportunityResendClientMagicLinksEngagementSheet
        sheetOpen={sheetOpen}
        setSheetOpen={setSheetOpen}
        qualifyingClients={qualifyingClients}
        clientsEligibleForReminder={clientsEligibleForReminder}
        buttonTrigger={
          <EngagementSheetPrimaryBtn
            priorityStatus={priorityStatus}
            onClick={() => setSheetOpen(true)}
            actionText={`View Clients`}
          />
        }
      />
    ),
    [
      sheetOpen,
      setSheetOpen,
      qualifyingClients,
      clientsEligibleForReminder,
      priorityStatus,
    ],
  );

  if (priorityStatus === 'Not applicable') return null;
  return (
    <OpportunityCard
      priorityStatus={priorityStatus}
      isLoading={status === 'fetchingClients'}
      data-testid="opportunity-card-access-link"
      cardContent={
        <OpportunityCardContent
          icon={
            <Eye
              className="h-8 w-8"
              strokeWidth={1}
            />
          }
          primaryMetric={
            qualifyingClients ? qualifyingClients.length.toString() : '0'
          }
          metricDescription="Clients who haven't used their unique access link."
          secondaryMetric={
            clientsEligibleForReminder
              ? clientsEligibleForReminder.length.toString()
              : '0'
          }
          secondaryMetricDescription={`${
            clientsEligibleForReminder
              ? clientsEligibleForReminder.length === 1
                ? 'client needs a reminder'
                : 'clients need reminders'
              : '0'
          }`}
        />
      }
      cardFooterContent={engagementSheet}
    />
  );
}

function useEmailTemplateState(advisor: Advisor) {
  const [advisorProvidedBody, setAdvisorProvidedBody] = useState<
    string | undefined
  >(undefined);
  const [isEditMessageOpen, setIsEditMessageOpen] = useState(false);

  const handleUpdateMessage = (body: string) => {
    setAdvisorProvidedBody(body);
    setIsEditMessageOpen(false);
  };

  return {
    advisorProvidedBody,
    isEditMessageOpen,
    setIsEditMessageOpen,
    handleUpdateMessage,
  };
}

function OpportunityResendClientMagicLinksEngagementSheet({
  sheetOpen,
  setSheetOpen,
  qualifyingClients,
  clientsEligibleForReminder,
  buttonTrigger,
}: {
  sheetOpen: boolean;
  setSheetOpen: (open: boolean) => void;
  qualifyingClients: Client[];
  clientsEligibleForReminder: Client[];
  buttonTrigger: React.ReactNode;
}) {
  const { advisor, clientsIsLoading, status } = useOpportunityState(
    'RESEND_CLIENT_SIGNIN_LINKS',
  );
  const [selectedClients, setSelectedClients] = useState<Client[]>([]);

  const { advisorProvidedBody, isEditMessageOpen, setIsEditMessageOpen } =
    useEmailTemplateState(advisor);

  useEffect(() => {
    if (!sheetOpen) {
      setSelectedClients([]);
    }
  }, [sheetOpen]);

  const isLoading = status === 'sendingEmails';

  const memoizedDataTable = useMemo(
    () => (
      <DataTable<Client>
        key={qualifyingClients.map(c => c.clientId).join('-')}
        columns={
          createOpportunityClientsTableColumns(
            'RESEND_CLIENT_SIGNIN_LINKS',
          ) as ColumnDef<Client>[]
        }
        getRowId={row => row.clientId}
        data={qualifyingClients}
        isLoading={clientsIsLoading}
        className="mb-12"
        tableId="sendReminderTable"
        variant="outlined"
        tableSkeletonRows={10}
        defaultSelection={clientsEligibleForReminder.filter(
          client => client.clientEmail,
        )}
        pageSize={8}
        hideColumnSelector={true}
        onSelectionChange={setSelectedClients}
        isRowDeactivated={row => !row.clientEmail}
      />
    ),
    [qualifyingClients, clientsIsLoading, clientsEligibleForReminder],
  );

  return (
    <>
      <OpportunityEngagementSheet
        trigger={buttonTrigger}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        title="Clients who have not viewed their results"
        description="Clients who completed their intake forms but haven't accessed their results. Auto-selected for next steps."
        clientsTable={memoizedDataTable}
        floatingMenuCtaProps={{
          text: `Review and send reminder`,
          onClick: () => setIsEditMessageOpen(true),
          disabled: isLoading,
        }}
        selectedClientCount={selectedClients.length}
      />

      <SendOpportunityMessageModal
        isOpen={isEditMessageOpen}
        onClose={() => setIsEditMessageOpen(false)}
        advisor={advisor}
        defaultMessage={advisorProvidedBody}
        emailTemplate={reviewOnboardingSlidesReminderWithLinkTemplate}
        emailTemplateProps={{
          clientName:
            selectedClients.length === 1
              ? selectedClients[0].clientFirstName
              : '{{Client Name}}',
          clientSigninLink: '#',
          emailPreferencesLink: '#',
          isPreview: true,
          advisorEmail: advisor.advisorEmail,
          advisorFirstName: advisor.advisorFirstName,
          advisorLastName: advisor.advisorLastName,
        }}
        selectedClients={selectedClients}
        engagementType="RESEND_CLIENT_SIGNIN_LINKS"
      />
    </>
  );
}
